import classnames from 'classnames'
import { useState } from 'react'

import MonitoringDonutReport from './MonitoringDonutReport'
import { Button, Card, IconChevronUp, ManufacturersGraphReport, StatusGuidePopup, Tooltip } from '@/components'
import { useAnalytics } from '@/hooks'
import { InverterManufacturer } from '@/models'
import { MonitoringSummaryDataType, FormChangeEventHandler } from '@/types'

type MonitoringReportProps = {
    summary: MonitoringSummaryDataType

    onChange: FormChangeEventHandler
}

const MonitoringReport = ({
    summary,
    ...props
}: MonitoringReportProps) => {
    const { trackEvent } = useAnalytics()
    const [isExpanded, setIsExpanded] = useState(true)

    const handleExpandToggleClick = () => {
        trackEvent('button_click', 'monitoring', 'Toggle Monitoring Report Section')
        setIsExpanded(isExpanded => !isExpanded)
    }

    const handleManufacturerClick = (manufacturer: InverterManufacturer) => {
        trackEvent('button_click', 'monitoring', `Supported Inverter Manufacturers Buttons ${manufacturer.key}`)
        props.onChange({
            target: {
                name: 'manufacturers',
                value: [manufacturer.key],
                displayValue: [manufacturer.title]
            }
        })
    }

    return <div className="flex flex-wrap lg:flex-nowrap gap-6 mb-4">
        <Card className="flex flex-col justify-between items-start min-w-full lg:min-w-100">
            <h3
                className="text-gray-500 text-sm"
                data-test="monitoring-report-left-section-title"
            >
                Top Inverter Manufacturer{isExpanded ? 's' : ''}
            </h3>

            <ManufacturersGraphReport
                summary={summary}
                isExpanded={isExpanded}
                onChange={props.onChange}
            />

            <Button
                design="btn-link"
                onClick={handleExpandToggleClick}
                className="mt-2 gap-2"
                data-test="monitoring-report-left-section-toggle-button"
            >
                <IconChevronUp className={`stroke-black transition-all ${!isExpanded ? 'rotate-180' : ''}`}/>
                See {isExpanded ? 'Less' : 'More'}
            </Button>
        </Card>

        <Card className="grow flex flex-col gap-4 justify-between items-start">
            <h3
                data-test="monitoring-report-right-section-title"
                className="text-gray-500 text-sm flex gap-4 justify-between w-full z-10"
            >
                Site Status Alert Prioritization

                <StatusGuidePopup compact={!isExpanded}/>
            </h3>

            <MonitoringDonutReport
                summary={summary}
                isExpanded={isExpanded}
                onChange={props.onChange}
            />

            <div className="flex justify-between w-full">
                <Button
                    design="btn-link"
                    onClick={handleExpandToggleClick}
                    className="gap-2"
                    data-test="monitoring-report-right-section-toggle-button"
                >
                    <IconChevronUp className={`stroke-black transition-all ${!isExpanded ? 'rotate-180' : ''}`}/>
                    See {isExpanded ? 'Less' : 'More'}
                </Button>

                {isExpanded && <Tooltip content="Currently Supported Integrations">
                    <div
                        className="flex w-fit"
                        data-test="monitoring-report-right-section-manufacturers"
                        onMouseEnter={() => {
                            trackEvent('element_hover', 'monitoring', 'Supported Inverter Manufacturers Buttons')
                        }}
                    >
                        {InverterManufacturer.allSupported.map((item, index) =>
                            !!summary.manufacturers[item.key] && <button
                                key={item.key}
                                data-test={`monitoring-report-right-section-manufacturers-${item.key}`}
                                onClick={() => handleManufacturerClick(item)}
                                className={classnames(
                                    'p-1.5 rounded-full border border-gray-300 bg-white hover:z-10 cursor-pointer',
                                    { '-ml-1.5': index }
                                )}
                            >
                                {item.getLogo()}
                            </button>)}
                    </div>
                </Tooltip>}
            </div>
        </Card>
    </div>
}

export default MonitoringReport
